import { Navigate, Route, Routes } from "react-router-dom";

import { getAccessToken } from "../lib/api.js";
import { HR_ROLES, MGR_ROLES } from "@hrms/types";

import ReviewListPage from "./performance/ReviewListPage.js";
import ReviewDetailPage from "./performance/ReviewDetailPage.js";
import PipPanel from "./performance/PipPanel.js";
import OrgGoalsPanel from "./performance/OrgGoalsPanel.js";
import { usePageBack } from "../context/PageBackContext.js";

function decodeToken(token: string): { role: string; sub: string } {
  try {
    const p = JSON.parse(atob(token.split(".")[1]!)) as Record<string, unknown>;
    return { role: String(p["role"] ?? ""), sub: String(p["sub"] ?? "") };
  } catch {
    return { role: "", sub: "" };
  }
}

// Mounted at /performance/* in App.tsx. The sub-routes here are what the
// sidebar's Performance sub-items link to.
export default function PerformancePage() {
  usePageBack("/dashboard");
  const { role } = decodeToken(getAccessToken() ?? "");
  const isHR = HR_ROLES.has(role);
  const isMgr = MGR_ROLES.has(role);
  const canManage = isHR || isMgr;

  return (
    <Routes>
      <Route path="reviews/:id" element={<ReviewDetailPage />} />
      <Route
        path="pip"
        element={
          <Shell title="Performance Improvement Plans" subtitle="PIP records and their weekly check-ins.">
            <PipPanel canManage={canManage} isHR={isHR} />
          </Shell>
        }
      />
      <Route
        path="skills"
        // The skills catalog moved to Admin → Skills; keep old links working.
        element={<Navigate to={isHR ? "/admin?tab=Skills" : "/performance"} replace />}
      />
      <Route
        path="goals"
        element={
          <Shell title="Goals" subtitle="Goals assigned to employees for a fiscal year.">
            <OrgGoalsPanel canEdit={canManage} isHR={isHR} />
          </Shell>
        }
      />
      <Route
        index
        element={
          <Shell
            title="Performance Reviews"
            subtitle="Annual reviews, including each new joiner's probation, First 30 Days review and outcome."
          >
            <ReviewListPage isHR={isHR} canCreate />
          </Shell>
        }
      />
      <Route path="*" element={<Navigate to="/performance" replace />} />
    </Routes>
  );
}

function Shell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="w-full p-8">
      <div className="mb-6">
        <h1 className="mb-1 text-[22px] font-bold">{title}</h1>
        <p className="text-[13px] text-surface-500">{subtitle}</p>
      </div>
      {children}
    </div>
  );
}
